import type {
  ReploAgent,
  SandboxFile,
} from "@agent-ui/features/chat/hooks/useReploAgentConnection";

import * as React from "react";

import { useSandboxFileActions } from "@agent-ui/hooks/useSandboxFileActions";
import { Button } from "@replo/design-system/components/button/Button";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { Copy, Ellipsis, Trash } from "lucide-react";

interface SandboxPageCardMenuProps {
  file: SandboxFile;
  agent: ReploAgent;
}

const SandboxPageCardMenu: React.FC<SandboxPageCardMenuProps> = ({
  file,
  agent,
}) => {
  const projectId = useCurrentProjectId();
  const { duplicateFile, deleteFile } = useSandboxFileActions({
    agent,
    projectId: projectId ?? null,
  });

  const handleDuplicate = () => {
    const baseRoute = file.route === "/" ? "/home" : file.route;
    const newRoute = `${baseRoute}-copy`;

    duplicateFile.mutate({ route: file.route, newRoute });
  };

  const handleDelete = () => {
    if (file.route === "/") {
      return;
    }

    // TODO: Add confirmation modal
    deleteFile.mutate({ route: file.route });
  };

  const isRootPage = file.route === "/";

  const deletePageItem = (
    <MenuItem
      label="Delete Page"
      variant="destructive"
      startEnhancer={<Trash />}
      disabled={isRootPage || deleteFile.isPending}
      onClick={handleDelete}
    />
  );

  return (
    <Menu>
      <MenuTrigger
        render={
          <Button
            icon
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Ellipsis />
          </Button>
        }
      />
      <MenuContent align="start" side="bottom">
        <MenuItem
          label="Duplicate Page"
          startEnhancer={<Copy />}
          disabled={duplicateFile.isPending}
          onClick={handleDuplicate}
        />
        <MenuSeparator />
        {isRootPage ? (
          <Tooltip content="Cannot delete the home page" side="right">
            {deletePageItem}
          </Tooltip>
        ) : (
          deletePageItem
        )}
      </MenuContent>
    </Menu>
  );
};

export default SandboxPageCardMenu;
